import Button from "@material-ui/core/Button/Button";
import Grid from "@material-ui/core/Grid/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateEmail } from "./hooks/useUpdateEmail";
import { emailMustMatch, emailRegex } from "@utils/formControl";
import { useWatch, Controller } from "react-hook-form";
import { Loader } from "@common/handleErrorAndLoading/Loader";

interface IParams {
    oldEmail: string;
    handleCancelClick(): void;
    onFinishUpdate(): void;
}

export const UpdateUserEmail = (props: IParams) => {
    const { oldEmail, handleCancelClick, onFinishUpdate } = props;
    const { formMethods, handleOnSubmit, changeEmailState } = useUpdateEmail(oldEmail, onFinishUpdate);
    const { t } = useTranslation(["profile", "glossary", "common", "validation"]);

    const { errors, control } = formMethods;

    const email = useWatch<string>({ name: "email", control: formMethods.control });

    return (
        <Grid container>
            <form className="mydetail-email-form" onSubmit={handleOnSubmit}>
                <Grid item sm={8} xs={12}>
                    <Controller
                        control={control}
                        name="email"
                        render={(renderProps) => (
                            <TextField
                                disabled={changeEmailState.isLoading}
                                error={Boolean(errors.email)}
                                fullWidth
                                helperText={errors.email?.message}
                                id="email"
                                inputRef={renderProps.ref}
                                label={`${t("profile:newEmailAddress")} *`}
                                margin="dense"
                                name={renderProps.name}
                                type="email"
                                onChange={renderProps.onChange}
                            />
                        )}
                        rules={{
                            required: t("validation:required"),
                            validate: (value: string) => emailRegex(value),
                        }}
                    />
                </Grid>
                <Grid item sm={8} xs={12}>
                    <Controller
                        control={control}
                        name="emailConfirm"
                        render={(renderProps) => (
                            <TextField
                                disabled={changeEmailState.isLoading}
                                error={Boolean(errors.emailConfirm)}
                                fullWidth
                                helperText={errors.emailConfirm?.message}
                                id="email-confirm"
                                inputRef={renderProps.ref}
                                label={`${t("profile:confirmNewEmailAddress")} *`}
                                margin="dense"
                                name={renderProps.name}
                                type="email"
                                onChange={renderProps.onChange}
                            />
                        )}
                        rules={{
                            required: t("validation:required"),
                            validate: (value: string) => emailRegex(value) ?? emailMustMatch(value, email),
                        }}
                    />
                </Grid>
                <Grid className="mydetail-email__info" item sm={8} xs={12}>
                    <Typography variant="body1">{t("profile:rememberHelper")}</Typography>
                </Grid>
                {changeEmailState.error && (
                    <Grid className="mydetail-email__info" item sm={8} xs={12}>
                        <Typography color="error" variant="body1">
                            {changeEmailState.error.errorMessage}
                        </Typography>
                    </Grid>
                )}
                <Grid item sm={8} xs={12}>
                    <Grid className="mydetail-email__buttons" container spacing={2}>
                        {changeEmailState.isLoading && <Loader size={40} />}
                        {!changeEmailState.isLoading && (
                            <Fragment>
                                <Grid item xs={4}>
                                    <Button color="secondary" fullWidth variant="outlined" onClick={handleCancelClick}>
                                        {t("common:cancel")}
                                    </Button>
                                </Grid>
                                <Grid item xs={4}>
                                    <Button
                                        color="secondary"
                                        disabled={oldEmail === email}
                                        formNoValidate
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                    >
                                        {t("glossary:save")}
                                    </Button>
                                </Grid>
                            </Fragment>
                        )}
                    </Grid>
                </Grid>
            </form>
        </Grid>
    );
};
